
import React from "react";
import { Project } from "@/types";
import { Card, CardContent } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

interface ProjectProgressChartProps {
  project: Project;
}

export default function ProjectProgressChart({ project }: ProjectProgressChartProps) {
  // Generate certainty data based on project stages
  const generateCertaintyData = () => {
    const data = project.stages.map(stage => {
      // Calculate certainty percentage based on stage status
      let certainty = 0;
      
      if (stage.status === "completed") {
        certainty = 100;
      } else if (stage.status === "in-progress") {
        // For in-progress stages, base certainty on completion percentage
        certainty = stage.progress;
      }
      
      // For risk-based certainty, include the risk factor
      // Higher risks reduce certainty
      const stageRisks = project.risks.filter(r => r.stage === stage.id && r.status !== "closed");
      const riskFactor = stageRisks.length > 0 
        ? stageRisks.reduce((sum, risk) => sum + (risk.probability * risk.impact), 0) / (stageRisks.length * 25)
        : 0;
      
      const riskAdjustedCertainty = Math.max(0, certainty * (1 - riskFactor * 0.5));
      
      return {
        name: `Stage ${stage.id}`,
        actual: certainty,
        riskAdjusted: Math.round(riskAdjustedCertainty),
        target: stage.id <= project.currentStage ? 100 : 0
      };
    });
    
    return data;
  };

  const chartData = generateCertaintyData();
  
  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis unit="%" domain={[0, 100]} />
          <Tooltip 
            formatter={(value: number) => [`${value}%`, ""]}
            labelFormatter={(label) => `${label}`}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="actual" 
            stroke="#3b82f6" 
            name="Actual Certainty"
            activeDot={{ r: 8 }} 
          />
          <Line 
            type="monotone" 
            dataKey="riskAdjusted" 
            stroke="#f59e0b" 
            name="Risk-Adjusted Certainty" 
          />
          <Line 
            type="monotone" 
            dataKey="target" 
            stroke="#10b981" 
            name="Target Certainty"
            strokeDasharray="5 5" 
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
